import React, { createContext, useImperativeHandle, useRef, useState, useEffect } from 'react'
import BaseModal from '@/component/modal/BaseModal.tsx'
import useQueryGetAlertEmergencyLive, { IAlertEmergencyLiveItem, IResGetEmergencyLive } from '@/api/notification/getLiveEmergencyAlert.ts'
import useQueryGetAlertFallDetectionLive, { IAlertFallDetectionLiveItem, IFallDetectionLiveData } from '@/api/notification/getLiveFallDetectionAlert.ts'
import useQueryGetAlertEmergencyDetail from '@/api/notification/getAlertEmergencyDetail.ts'
import useQueryGetFallDetectionDetail from '@/api/notification/getFallDetectionDetail.ts'
import EmergencyAlertItem from '@/pages/notification/emergency/EmergencyAlertItem.tsx'
import EmergencyAlertDetail from '@/pages/notification/emergency/EmergencyAlertDetail.tsx'
import { isEmptyArray, isNotEmptyArray } from '@/util/array.ts'
import { useTranslation } from 'react-i18next'
import { EmergencyAlertType } from '@/api/notification/getLiveEmergencyAlert.ts'
import { EmergencyAlertConfirmStatus } from '@/api/notification/getNotificationSos.ts'
import { useAudioStore } from '@/store/AudioStore.ts'
import { useDismissedAlertStore } from '@/store/DismissedAlertStore.ts'
import { useAlarmSound } from '@/store/AlarmSoundStore.ts'
import {
    IEmergencyAlertSelection,
    selectLatestUnprocessedAlert,
    sortAlertsNewestFirst
} from '@/util/emergencyAlertSelection.ts'
import MapSwitcher from '@/component/map/MapSwitcher.tsx'
import { decideLiveAlertSync, ILiveAlertSignature, toLiveAlertSignature } from '@/util/emergencyAlertLiveSync.ts'
import FallIcon from '@/assets/ic_fall.svg?react'


export interface IProps {
    className?: string
    refetchAlerts?: () => void
    defaultId?: number
    defaultTab?: EmergencyAlertType
    sosAlerts?: IResGetEmergencyLive
    fallAlerts?: IFallDetectionLiveData
    isLiveMode?: boolean
}

export type IEmergencyLiveAlertContextProps = {
    id?: number
    setId: (id: number) => void
    refetchList: () => void
    refetchAndResetSelection: () => Promise<boolean>
    activeTab: EmergencyAlertType
    setActiveTab: (tab: EmergencyAlertType) => void
    isLiveMode: boolean
    closeModal: () => void
}

export const EmergencyLiveAlertContext = createContext<IEmergencyLiveAlertContextProps>({
    id: undefined,
    setId: () => {
    },
    refetchList: () => {
    },
    refetchAndResetSelection: async () => false,
    activeTab: EmergencyAlertType.SOS,
    setActiveTab: () => {
    },
    isLiveMode: true,
    closeModal: () => {
    }
})

const EmergencyAlertModal = ({ defaultId, refetchAlerts, defaultTab, sosAlerts: propSosAlerts, fallAlerts: propFallAlerts, isLiveMode = true }: IProps, ref: any) => {
    const { t } = useTranslation()
    const { play, stop } = useAudioStore()
    const { dismissAlerts } = useDismissedAlertStore()
    const { enabled: isAlarmSoundOn } = useAlarmSound()

    const modalRef = useRef<any>()
    const pendingSelectionRef = useRef<IEmergencyAlertSelection>()
    const [id, setId] = useState<number>()
    const [activeTab, setActiveTab] = useState<EmergencyAlertType>(defaultTab || EmergencyAlertType.SOS)
    const [isUserTabChange, setIsUserTabChange] = useState(false)
    const [isModalOpen, setIsModalOpen] = useState(false)

    // SOS 알림 데이터
    const { data: liveSosAlerts, refetch: refetchSosList } = useQueryGetAlertEmergencyLive(isLiveMode)
    // 낙상 감지 알림 데이터
    const { data: liveFallAlerts, refetch: refetchFallList } = useQueryGetAlertFallDetectionLive(isLiveMode)

    const sosAlerts = isLiveMode ? (propSosAlerts ?? liveSosAlerts) : propSosAlerts
    const fallAlerts = isLiveMode ? (propFallAlerts ?? liveFallAlerts) : propFallAlerts

    const { data: emergencyDetail, refetch: refetchEmergencyDetail } = useQueryGetAlertEmergencyDetail(
        activeTab === EmergencyAlertType.SOS ? id : undefined
    )
    const { data: fallDetail, refetch: refetchFallDetail } = useQueryGetFallDetectionDetail(
        activeTab === EmergencyAlertType.FALL ? id : undefined
    )


    // 모달이 열린 상태에서 선택 항목이 없다면 두 유형 중 가장 최근 이벤트를 선택한다.
    useEffect(() => {
        if (isLiveMode && isModalOpen && !isUserTabChange && id === undefined) {
            const latestAlert = selectLatestUnprocessedAlert(sosAlerts?.emergencies, fallAlerts?.falls)
            if (latestAlert) {
                setActiveTab(latestAlert.type)
                setId(latestAlert.id)
            }
        }
    }, [sosAlerts, fallAlerts, isLiveMode, isModalOpen, id, isUserTabChange])

    const refetchList = () => {
        if (activeTab === EmergencyAlertType.SOS) {
            refetchSosList()
        } else {
            refetchFallList()
        }
    }

    const getFilteredAlerts = (): (IAlertEmergencyLiveItem | IAlertFallDetectionLiveItem)[] => {
        if (activeTab === EmergencyAlertType.SOS) {
            if (!sosAlerts?.emergencies || sosAlerts.emergencies.length === 0) return []
            return sortAlertsNewestFirst(sosAlerts.emergencies.filter(item =>
                item?.alert?.type === EmergencyAlertType.SOS || !item?.alert?.type
            ))
        } else {
            if (!fallAlerts?.falls || fallAlerts.falls.length === 0) return []
            return sortAlertsNewestFirst(fallAlerts.falls)
        }
    }

    const hasAnyAlerts = (): boolean => {
        const sosAlertCount = sosAlerts?.emergencies?.filter(item =>
            item?.alert?.type === EmergencyAlertType.SOS || !item?.alert?.type
        ).length || 0;
        const fallAlertCount = fallAlerts?.falls?.length || 0;
        return sosAlertCount > 0 || fallAlertCount > 0;
    }

    const handleTabChange = (tab: EmergencyAlertType) => {
        setIsUserTabChange(true)
        setActiveTab(tab)
        setId(undefined)
    }

    const closeModal = () => {
        modalRef.current?.close()
    }

    const resolveSelection = (newAlerts: any[]): boolean => {
        // activeTab에 따라 필터링 로직이 달라질 수 있음
        const filteredAlerts = activeTab === EmergencyAlertType.SOS
            ? newAlerts?.filter((item: IAlertEmergencyLiveItem) =>
                item?.alert?.type === EmergencyAlertType.SOS || !item?.alert?.type
            )
            : newAlerts;

        if (isEmptyArray(filteredAlerts) && !hasAnyAlerts()) {
            // modalRef.current.close() // <--- 이 부분을 주석 처리하여 자동으로 닫히지 않도록 합니다.
            return true
        }

        // 현재 탭의 알림이 없고 사용자가 직접 탭을 변경하지 않은 경우에만 자동 전환
        if (isEmptyArray(filteredAlerts) && !isUserTabChange) {
            const otherTabHasAlerts = activeTab === EmergencyAlertType.SOS
                ? (fallAlerts?.falls && fallAlerts.falls.length > 0)
                : (sosAlerts?.emergencies && sosAlerts.emergencies.length > 0)

            if (otherTabHasAlerts) {
                setActiveTab(activeTab === EmergencyAlertType.SOS ? EmergencyAlertType.FALL : EmergencyAlertType.SOS)
                return true
            }
        }

        setIsUserTabChange(false)

        const myAlerts = filteredAlerts?.filter((item: IAlertEmergencyLiveItem | IAlertFallDetectionLiveItem) => item?.alert?.isMyTask == true)
        if (myAlerts && isNotEmptyArray(myAlerts)) {
            setId(myAlerts[0].id)
            return myAlerts[0].id !== id
        }

        const notMyAlerts = filteredAlerts?.filter((item: IAlertEmergencyLiveItem | IAlertFallDetectionLiveItem) => !item?.alert?.taskManagerAt)
        if (notMyAlerts && isNotEmptyArray(notMyAlerts)) {
            setId(notMyAlerts[0].id)
            return notMyAlerts[0].id !== id
        }

        if (isNotEmptyArray(filteredAlerts)) {
            setId(filteredAlerts[0].id)
            return filteredAlerts[0].id !== id
        }

        setId(undefined)
        return true
    }

    const refetchAndResetSelection = async (): Promise<boolean> => {
        if (!isLiveMode) return false

        let newAlerts: any[] = []
        let resAlerts: any;

        if (activeTab === EmergencyAlertType.SOS) {
            const { data } = await refetchSosList()
            resAlerts = data
            newAlerts = resAlerts?.emergencies || []
        } else {
            const { data } = await refetchFallList()
            resAlerts = data
            newAlerts = resAlerts?.falls || []
        }

        return resolveSelection(newAlerts)
    }

    const advanceSelection = () => {
        if (!isLiveMode) return
        resolveSelection(activeTab === EmergencyAlertType.SOS
            ? sosAlerts?.emergencies || []
            : fallAlerts?.falls || [])
    }

    const selectedLiveItem = activeTab === EmergencyAlertType.SOS
        ? sosAlerts?.emergencies?.find(item => item.id === id)
        : fallAlerts?.falls?.find(item => item.id === id)
    const selectedLiveSignature = toLiveAlertSignature(selectedLiveItem)
    const selectedDetail = activeTab === EmergencyAlertType.SOS ? emergencyDetail : fallDetail
    const refetchSelectedDetail = activeTab === EmergencyAlertType.SOS ? refetchEmergencyDetail : refetchFallDetail
    const lastLiveSignatureRef = useRef<{ key: string; signature?: ILiveAlertSignature }>()

    useEffect(() => {
        if (!isLiveMode || !isModalOpen || id === undefined) {
            lastLiveSignatureRef.current = undefined
            return
        }

        const key = `${activeTab}:${id}`
        const previous = lastLiveSignatureRef.current?.key === key
            ? lastLiveSignatureRef.current.signature
            : undefined
        lastLiveSignatureRef.current = { key, signature: selectedLiveSignature }

        const action = decideLiveAlertSync(previous, selectedLiveSignature, selectedDetail)
        if (action === 'refetch-detail') {
            refetchSelectedDetail()
        } else if (action === 'advance-selection') {
            advanceSelection()
        }
    }, [
        isLiveMode,
        isModalOpen,
        id,
        activeTab,
        selectedLiveSignature !== undefined,
        selectedLiveSignature?.assignedAt,
        selectedLiveSignature?.isMyTask
    ])

    useImperativeHandle(ref, () => ({
        show: (selection?: number | IEmergencyAlertSelection) => {
            if (typeof selection === 'number') {
                setId(selection)
            } else if (selection) {
                pendingSelectionRef.current = selection
                setActiveTab(selection.type)
                setId(selection.id)
            } else if (isLiveMode) {
                const latestAlert = selectLatestUnprocessedAlert(sosAlerts?.emergencies, fallAlerts?.falls)
                pendingSelectionRef.current = latestAlert
                if (latestAlert) {
                    setActiveTab(latestAlert.type)
                    setId(latestAlert.id)
                }
            }
            modalRef.current.show()
        },
        close: () => {
            modalRef.current.close()
        }
    }))

    // activeTab이 변경될 때 ID를 초기화하고 목록을 다시 로드하여 첫 번째 항목을 선택하도록 합니다.
    useEffect(() => {
        if (isLiveMode && isModalOpen && id === undefined) {
            refetchAndResetSelection();
        }
    }, [activeTab, isLiveMode, isModalOpen, id]);

    // defaultId가 변경될 때 선택된 항목 업데이트
    useEffect(() => {
        if (defaultId !== undefined) {
            // 실시간 목록에서 이미 사라진 알림 ID를 먼저 조회하면 404가 발생한다.
            // 현재 목록에 남아 있는 경우에만 선택하고, 아니면 onOpen에서
            // 최신 첫 항목을 선택하도록 비워 둔다.
            const isDefaultAvailable = !isLiveMode
                || getFilteredAlerts().some(alert => alert.id === defaultId)
            setId(isDefaultAvailable ? defaultId : undefined);
            modalRef.current?.show();
        }
    }, [defaultId, isLiveMode]);

    useEffect(() => {
        if (isLiveMode && isModalOpen) {
            if (!hasAnyAlerts()) {
                modalRef.current?.close()
            }
        }
    }, [sosAlerts, fallAlerts, isLiveMode, isModalOpen])

    useEffect(() => {
        const detailData = activeTab === EmergencyAlertType.SOS ? emergencyDetail : fallDetail;
        if (isAlarmSoundOn && detailData?.alert?.confirm === EmergencyAlertConfirmStatus.WAIT) {
            play();
        } else {
            stop();
        }
    }, [id, emergencyDetail, fallDetail, activeTab, isAlarmSoundOn]);

    return (
        <EmergencyLiveAlertContext.Provider value={{
            id,
            setId,
            refetchList,
            refetchAndResetSelection,
            activeTab,
            setActiveTab: handleTabChange,
            isLiveMode,
            closeModal
        }}>
            <BaseModal
                ref={modalRef}
                width={'1200'}
                panelClassName={`emergency-console ${activeTab === EmergencyAlertType.FALL ? 'emergency-console--fall' : 'emergency-console--sos'}`}
                onOpenCallback={() => {
                    setIsUserTabChange(false)
                    setIsModalOpen(true)

                    if (isLiveMode) {
                        const pendingSelection = pendingSelectionRef.current
                        pendingSelectionRef.current = undefined
                        if (pendingSelection) {
                            setActiveTab(pendingSelection.type)
                            setId(pendingSelection.id)
                            return
                        }

                        const currentFilteredAlerts = getFilteredAlerts()
                        if (defaultId && currentFilteredAlerts.find(alert => alert.id === defaultId)) {
                            setId(defaultId)
                        } else if (isNotEmptyArray(currentFilteredAlerts)) {
                            setId(currentFilteredAlerts[0]!.id)
                        } else {
                            // 현재 탭에 알림이 없을 때 다른 탭 확인
                            const otherTabHasAlerts = activeTab === EmergencyAlertType.SOS
                                ? fallAlerts?.falls
                                : sosAlerts?.emergencies

                            if (isNotEmptyArray(otherTabHasAlerts)) {
                                setActiveTab(activeTab === EmergencyAlertType.SOS ? EmergencyAlertType.FALL : EmergencyAlertType.SOS)
                            } else {
                                setId(undefined)
                            }
                        }
                    } else {
                        // 행 클릭 시 show(id)로 예약한 최신 ID를 유지한다.
                        // ID 없이 외부에서 연 경우에만 defaultId를 사용한다.
                        setId(currentId => currentId ?? defaultId)
                    }
                }}
                onCloseCallback={() => {
                    const unprocessedAlertIds = [
                        ...(sosAlerts?.emergencies || []),
                        ...(fallAlerts?.falls || [])
                    ].map(alert => alert.id)
                    if (unprocessedAlertIds.length > 0) {
                        dismissAlerts(unprocessedAlertIds)
                    }

                    stop()
                    setId(undefined)
                    setIsUserTabChange(false)
                    setIsModalOpen(false)
                    refetchAlerts && refetchAlerts()
                }}>

                <header className="emergency-console__header">
                    <div className="emergency-console__header-icon" aria-hidden>
                        {activeTab === EmergencyAlertType.FALL ? <FallIcon aria-hidden /> : '!'}
                    </div>
                    <div>
                        <p className="emergency-console__eyebrow">CAREARLY RESPONSE CENTER</p>
                        <h2>{activeTab === EmergencyAlertType.FALL ? t('notification.v2.emergencyModal.fallTitle') : t('notification.v2.emergencyModal.sosTitle')}</h2>
                    </div>
                    <div className="emergency-console__header-actions">
                        <MapSwitcher className="emergency-console__map-switcher" />
                        <div className="emergency-console__header-status">
                            <span className="emergency-console__pulse" />
                            {t('notification.v2.emergencyModal.liveMonitoring')}
                        </div>
                    </div>
                </header>

                <div className="emergency-console__body">
                    <div className={`emergency-console__layout ${!isLiveMode ? 'emergency-console__layout--detail' : ''}`}>
                        {/* Live 목록 */}
                        {isLiveMode && <aside className="emergency-console__rail">
                            <div className="emergency-console__tabs" role="tablist" aria-label={t('notification.v2.alertType')}>
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={activeTab === EmergencyAlertType.SOS}
                                    className={`emergency-console__tab ${activeTab === EmergencyAlertType.SOS ? 'is-active' : ''}`}
                                    onClick={() => handleTabChange(EmergencyAlertType.SOS)}>
                                    <span>!</span>
                                    {t('notification.sosModal.tab.sos')}
                                    <strong>{sosAlerts?.emergencies?.length || 0}</strong>
                                </button>
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={activeTab === EmergencyAlertType.FALL}
                                    className={`emergency-console__tab emergency-console__tab--fall ${activeTab === EmergencyAlertType.FALL ? 'is-active' : ''}`}
                                    onClick={() => handleTabChange(EmergencyAlertType.FALL)}>
                                    <span><FallIcon aria-hidden /></span>
                                    {t('notification.sosModal.tab.fall')}
                                    <strong>{fallAlerts?.falls?.length || 0}</strong>
                                </button>
                            </div>

                            <div className="emergency-console__queue-heading">
                                <div>
                                    <strong>{t('notification.v2.emergencyModal.pendingQueue')}</strong>
                                    <span>{t('notification.v2.emergencyModal.sortedByRecent')}</span>
                                </div>
                                <b>{getFilteredAlerts().length}</b>
                            </div>

                            <div className="app-scrollbar emergency-console__queue">
                                {getFilteredAlerts().length > 0
                                    ? getFilteredAlerts().map((item: IAlertEmergencyLiveItem | IAlertFallDetectionLiveItem) =>
                                        <EmergencyAlertItem
                                            key={item?.id}
                                            item={item}
                                            alertType={activeTab}
                                            />)
                                    : <div className="emergency-console__empty">
                                        <span>✓</span>
                                        <p>{t('notification.sosModal.empty')}</p>
                                    </div>}
                            </div>
                        </aside>}

                        {/* 알림 상세 내용 */}
                        <EmergencyAlertDetail />
                    </div>
                </div>
            </BaseModal>
        </EmergencyLiveAlertContext.Provider>
    )
}

export default React.forwardRef(EmergencyAlertModal)
